import React, { useState } from 'react'

/* ─────────────────────────────────────────────────────────────
   Airport Services — Service Request Form
   Can be booked standalone or as part of a flight request
───────────────────────────────────────────────────────────── */

const SERVICES = [
  { id: 'arrival',   label: 'Arrival Meet & Greet',  desc: 'Our representative greets you at the aircraft or gate' },
  { id: 'departure', label: 'Departure Escort',       desc: 'Full escort through check-in, security and to your gate' },
  { id: 'transfer',  label: 'Private Transfer',       desc: 'Private vehicle to or from the airport'                 },
]

const VEHICLE_OPTIONS = [
  { id: 'sedan',   label: 'Executive Sedan'     },
  { id: 'suv',     label: 'Luxury SUV'           },
  { id: 'van',     label: 'Executive Van'         },
  { id: 'maybach', label: 'Maybach / Ultra Luxury'},
]

function ConfirmRow({ label, value }) {
  return (
    <div className="svc-confirm__row">
      <span className="svc-confirm__key">{label}</span>
      <span className="svc-confirm__val">{value}</span>
    </div>
  )
}

export default function AirportServices({ onBack, member }) {
  const [submitted,    setSubmitted]    = useState(false)
  const [showConfirm,  setShowConfirm]  = useState(false)

  const [airport,      setAirport]      = useState('')
  const [flightNumber, setFlightNumber] = useState('')
  const [date,         setDate]         = useState('')
  const [selectedSvc,  setSelectedSvc]  = useState([])
  const [passengers,   setPassengers]   = useState(1)
  const [vehicle,      setVehicle]      = useState('sedan')
  const [specialReqs,  setSpecialReqs]  = useState('')

  const toggleService = (id) => {
    setSelectedSvc(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    )
  }

  const needsVehicle = selectedSvc.includes('transfer')

  const handleSubmit = (e) => {
    e.preventDefault()
    setShowConfirm(true)
  }

  const handleConfirm = () => {
    setShowConfirm(false)
    setSubmitted(true)
  }

  const selectedLabels = selectedSvc
    .map(id => SERVICES.find(s => s.id === id)?.label)
    .filter(Boolean)
    .join(', ')

  if (submitted) {
    return (
      <div className="db-page svc-page">
        <div className="svc-submitted">
          <div className="svc-submitted__icon" aria-hidden="true">✦</div>
          <h2 className="svc-submitted__heading">Request Received</h2>
          <p className="svc-submitted__body">
            Your request has been received. We will be in touch within the hour.
          </p>
          <p className="svc-submitted__sig">— Mwuïlu</p>
          <button className="svc-btn-ghost" onClick={onBack}>Return to Dashboard</button>
        </div>
      </div>
    )
  }

  if (showConfirm) {
    return (
      <div className="db-page svc-page">
        <div className="svc-confirm">
          <p className="svc-confirm__overline">Please review your request</p>
          <h2 className="svc-confirm__heading">Airport Services</h2>
          <div className="svc-confirm__rows">
            <ConfirmRow label="Airport"       value={airport      || '—'} />
            <ConfirmRow label="Flight number" value={flightNumber || '—'} />
            <ConfirmRow label="Date"          value={date         || '—'} />
            <ConfirmRow label="Services"      value={selectedLabels || '—'} />
            <ConfirmRow label="Passengers"    value={`${passengers} passenger${passengers !== 1 ? 's' : ''}`} />
            {needsVehicle && (
              <ConfirmRow label="Vehicle" value={VEHICLE_OPTIONS.find(v => v.id === vehicle)?.label || '—'} />
            )}
            {specialReqs && <ConfirmRow label="Special requirements" value={specialReqs} />}
          </div>
          <div className="svc-confirm__actions">
            <button type="button" className="svc-btn-ghost"   onClick={() => setShowConfirm(false)}>Edit</button>
            <button type="button" className="svc-btn-primary" onClick={handleConfirm}>Confirm & Submit</button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="db-page svc-page">

      <div className="svc-header">
        <button className="svc-back" onClick={onBack} aria-label="Back to dashboard">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Dashboard
        </button>
        <div className="svc-header__title-wrap">
          <p className="svc-header__overline">Service Request</p>
          <h1 className="svc-header__title">Airport Services</h1>
        </div>
      </div>

      <form className="svc-form" onSubmit={handleSubmit} noValidate>

        {/* ── Airport & flight ── */}
        <div className="svc-section">
          <p className="svc-section__label">Flight Details</p>
          <div className="svc-row-2">
            <div className="svc-field">
              <label className="svc-label" htmlFor="airport">Airport</label>
              <input
                id="airport"
                type="text"
                className="svc-input"
                value={airport}
                onChange={e => setAirport(e.target.value)}
                placeholder="Airport name or IATA code"
                required
              />
            </div>
            <div className="svc-field">
              <label className="svc-label" htmlFor="flightNum">Flight Number</label>
              <input
                id="flightNum"
                type="text"
                className="svc-input"
                value={flightNumber}
                onChange={e => setFlightNumber(e.target.value.toUpperCase())}
                placeholder="e.g. SN123"
              />
            </div>
          </div>
          <div className="svc-field" style={{ marginTop: '14px' }}>
            <label className="svc-label" htmlFor="airportDate">Date</label>
            <input id="airportDate" type="date" className="svc-input svc-input--half" value={date} onChange={e => setDate(e.target.value)} required />
          </div>
        </div>

        {/* ── Service selection ── */}
        <div className="svc-section">
          <p className="svc-section__label">Service Required</p>
          <div className="svc-service-cards">
            {SERVICES.map(svc => (
              <button
                key={svc.id}
                type="button"
                className={`svc-service-card${selectedSvc.includes(svc.id) ? ' svc-service-card--active' : ''}`}
                onClick={() => toggleService(svc.id)}
              >
                <div className="svc-service-card__check" aria-hidden="true">
                  {selectedSvc.includes(svc.id) ? '✓' : ''}
                </div>
                <div className="svc-service-card__content">
                  <span className="svc-service-card__label">{svc.label}</span>
                  <span className="svc-service-card__desc">{svc.desc}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* ── Passengers ── */}
        <div className="svc-section">
          <p className="svc-section__label">Number of Passengers</p>
          <div className="svc-steppers">
            <div className="svc-stepper">
              <span className="svc-stepper__label">Passengers</span>
              <div className="svc-stepper__controls">
                <button type="button" className="svc-stepper__btn" onClick={() => setPassengers(p => Math.max(1, p - 1))} disabled={passengers <= 1}>−</button>
                <span className="svc-stepper__value">{passengers}</span>
                <button type="button" className="svc-stepper__btn" onClick={() => setPassengers(p => Math.min(20, p + 1))}>+</button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Vehicle (only if transfer selected) ── */}
        {needsVehicle && (
          <div className="svc-section">
            <p className="svc-section__label">Vehicle Preference</p>
            <div className="svc-vehicle-grid">
              {VEHICLE_OPTIONS.map(v => (
                <button
                  key={v.id}
                  type="button"
                  className={`svc-vehicle-card${vehicle === v.id ? ' svc-vehicle-card--active' : ''}`}
                  onClick={() => setVehicle(v.id)}
                >
                  <span className="svc-vehicle-card__label">{v.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Special requirements ── */}
        <div className="svc-section">
          <div className="svc-field">
            <label className="svc-label" htmlFor="airportSpecial">Special Requirements</label>
            <textarea
              id="airportSpecial"
              className="svc-textarea"
              rows={3}
              value={specialReqs}
              onChange={e => setSpecialReqs(e.target.value)}
              placeholder="Accessibility needs, VIP requirements, specific requests…"
            />
          </div>
        </div>

        <div className="svc-form__footer">
          <button type="submit" className="svc-btn-primary svc-btn-primary--wide">
            Review Request
          </button>
        </div>

      </form>
    </div>
  )
}